import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { Compass, Link as LinkIcon } from "lucide-react";
import { logger } from "@/lib/logger";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { useTeamUsageQuery } from "@/hooks/useTeamUsageQuery";

const schema = z.object({
  zillowUrl: z
    .string()
    .min(1, "Zillow URL is required")
    .url("Must be a valid URL")
    .refine((url) => url.includes("zillow.com"), "Must be a zillow.com URL")
    .refine((url) => /\/\d+_zpid/.test(url), "URL must link to a specific property (e.g. zillow.com/homedetails/…)"),
  buyerName: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

interface CreateBuyerReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateBuyerReportDialog({ open, onOpenChange }: CreateBuyerReportDialogProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { getEffectiveUserId } = useImpersonation();
  const { canCreateBuyerReport } = useTeamUsageQuery();
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("Fetching listing from Zillow...");

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { zillowUrl: "", buyerName: "" },
  });

  useEffect(() => {
    if (open) {
      form.reset();
      setLoading(false);
    }
  }, [open, form]);

  const onSubmit = async (values: FormValues) => {
    if (!canCreateBuyerReport()) {
      toast({
        title: "Monthly limit reached",
        description: "You've used all your Buyer Reports this month. Upgrade your plan to create more.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    setLoadingMessage("Fetching listing from Zillow...");

    try {
      const zillowUrl = values.zillowUrl.trim();
      const zpidMatch = zillowUrl.match(/\/(\d+)_zpid/);
      const zpid = zpidMatch![1]; // schema already validated this exists

      // Get agent profile
      const effectiveUserId = await getEffectiveUserId();
      if (!effectiveUserId) throw new Error("Not authenticated");

      const { data: profile } = await supabase
        .from("profiles")
        .select("team_id, full_name, company_logo_url, default_primary_color, default_secondary_color")
        .eq("user_id", effectiveUserId)
        .maybeSingle();

      if (!profile?.team_id) throw new Error("Team not found. Please refresh and try again.");

      setLoadingMessage("Pulling property data...");

      // Scrape Zillow for property data
      const { data: scrapedData, error: scrapeError } = await supabase.functions.invoke(
        "scrape-zillow",
        { body: { zillowUrl } }
      );

      const propertyData = !scrapeError && scrapedData ? scrapedData : null;

      // Extract address from scraped data
      const address = propertyData?.address?.streetAddress || "";
      const city = propertyData?.address?.city || "";
      const state = propertyData?.address?.state || "";
      const zip = propertyData?.address?.zipcode || "";

      setLoadingMessage("Creating Buyer Report...");

      const { data: report, error: insertError } = await supabase
        .from("property_reports" as any)
        .insert([{
          user_id: effectiveUserId,
          team_id: profile.team_id,
          address,
          city,
          state,
          zip,
          buyer_name: values.buyerName || null,
          zpid,
          zillow_url: zillowUrl,
          property_data: propertyData || {},
          custom_agent_name: profile.full_name || null,
          custom_logo_url: profile.company_logo_url || null,
          primary_brand_color: profile.default_primary_color || "#0c0f24",
          secondary_brand_color: profile.default_secondary_color || "#1b489b",
        }])
        .select()
        .single();

      if (insertError) throw insertError;

      // Fetch full data (walk scores, FEMA, schools, etc.) in background
      supabase.functions
        .invoke("fetch-property-report-data", { body: { zpid, reportId: (report as any).id } })
        .catch((err) => logger.error("Failed to fetch full property data:", err));

      onOpenChange(false);
      navigate(`/buyer-report/${(report as any).id}`);
      toast({ title: "Buyer Report Created!", description: "Your report is ready to edit." });
    } catch (err) {
      logger.error("Error creating buyer report:", err);
      const msg = err instanceof Error ? err.message : "Failed to create report";
      toast({ title: "Error", description: msg, variant: "destructive" });
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Dialog open={true}>
        <DialogContent className="sm:max-w-[380px] gap-8 [&>button]:hidden">
          <div className="flex flex-col items-center space-y-5 py-6">
            <Compass className="h-12 w-12 text-primary animate-spin" style={{ animationDuration: "3s" }} />
            <div className="space-y-1 text-center">
              <h2 className="text-xl font-bold">Creating Buyer Report</h2>
              <p className="text-sm text-muted-foreground">{loadingMessage}</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center gap-2">
            <LinkIcon className="h-5 w-5" />
            New Buyer Report
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground -mt-1">
          Paste the Zillow listing URL for the property your buyer is considering.
        </p>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="zillowUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Zillow Listing URL</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="https://www.zillow.com/homedetails/..."
                      autoFocus
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="buyerName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Buyer Name <span className="text-muted-foreground font-normal">(optional)</span></FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. John & Jane Doe" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button type="submit" variant="gold" className="w-full gap-2">
              <LinkIcon className="h-4 w-4" />
              Create Buyer Report
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
